'use client'

import { useEffect, useState } from 'react'

interface ImageLoadState {
  src: string | undefined
  loaded: boolean
}

/**
 * Hook to preload image before rendering
 * Returns true when the image has been loaded successfully
 */
export function useImageLoaded(src: string | undefined): boolean {
  // Track both src and loaded state together to derive reset behavior
  const [state, setState] = useState<ImageLoadState>({ src, loaded: false })

  // When src changes, we need to reset - handled synchronously during render
  // by comparing and returning early with correct loaded status
  const needsReset = state.src !== src

  useEffect(() => {
    // Reset state when src changes
    if (needsReset) {
      setState({ src, loaded: false })
      return
    }

    if (!src) {
      return
    }

    const img = new Image()
    img.onload = () => setState((prev) => (prev.src === src ? { ...prev, loaded: true } : prev))
    img.onerror = () => setState((prev) => (prev.src === src ? { ...prev, loaded: false } : prev))
    img.src = src

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [src, needsReset])

  // Return false during reset, otherwise return actual loaded state
  return needsReset ? false : state.loaded
}
